import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import Layout from "../components/Layout/Layout";
import "../styles/PaymentModern.css";

const PayLink = () => {
  const { accessToken } = useParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPayment = async () => {
      try {
        const { data } = await axios.get(`/api/v1/payments/pay-link/${accessToken}`);
        if (data?.success) setPayment(data.payment);
        else setError(data.message || "رابط الدفع غير صالح");
      } catch (err) {
        setError(err.response?.data?.message || "رابط الدفع غير صالح");
      } finally {
        setLoading(false);
      }
    };
    if (accessToken) fetchPayment();
    else setLoading(false);
  }, [accessToken]);

  const handlePay = async () => {
    try {
      setPaying(true);
      const { data } = await axios.post(`/api/v1/payments/pay-link/${accessToken}/pay`);
      if (data?.success && data.paymentUrl) {
        window.location.href = data.paymentUrl;
      } else {
        toast.error(data?.message || "حدث خطأ");
        setPaying(false);
      }
    } catch (err) {
      const msg = err.response?.data?.message || "حدث خطأ في عملية الدفع";
      toast.error(msg);
      if (err.response?.data?.paymentStatus === "completed") {
        setPayment((prev) => prev ? { ...prev, paymentStatus: "completed" } : prev);
      }
      setPaying(false);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: `دفع طلب ${payment?.qi?.merchantReference || ""}`,
        text: `رابط دفع طلب من النخبة ستور بمبلغ ${payment?.totalAmount?.toLocaleString("ar-IQ")} د.ع`,
        url,
      }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url);
      toast.success("تم نسخ رابط الدفع");
    }
  };

  const formatPrice = (price) => (price || 0).toLocaleString("ar-IQ") + " د.ع";

  if (loading) {
    return (
      <Layout title="رابط الدفع - النخبة ستور">
        <div className="pm-page">
          <div className="pm-container">
            <div className="pm-loading">
              <div className="pm-spinner" />
              <p>جاري التحميل...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !payment) {
    return (
      <Layout title="رابط الدفع - النخبة ستور">
        <div className="pm-page">
          <div className="pm-container">
            <div className="pm-status-card pm-status-error">
              <div className="pm-status-icon-wrap pm-icon-error">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              </div>
              <h3>{error || "رابط الدفع غير صالح"}</h3>
              <button className="pm-btn pm-btn-primary" onClick={() => navigate("/")}>
                العودة للرئيسية
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const isPaid = payment.paymentStatus === "completed";
  const isProcessing = payment.paymentStatus === "processing";
  const isFailed = payment.paymentStatus === "failed" || payment.paymentStatus === "expired";

  return (
    <Layout title="رابط الدفع - النخبة ستور">
      <div className="pm-page">
        <div className="pm-container">

          {/* Store Header */}
          <div className="pm-header">
            <div className="pm-logo-glow" />
            <h1 className="pm-store-name">النخبة ستور</h1>
            <p className="pm-store-sub">
              {isPaid ? "تم الدفع بنجاح" : isProcessing ? "جاري معالجة الدفع" : "رابط دفع إلكتروني آمن"}
            </p>
            <div className="pm-header-line" />
          </div>

          {/* Status Banner */}
          {isPaid && (
            <div className="pm-banner pm-banner-success animate-fade-in">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              <span>تم دفع هذا الطلب بنجاح</span>
            </div>
          )}

          {isFailed && (
            <div className="pm-banner pm-banner-error animate-fade-in">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <span>فشلت عملية الدفع السابقة. يمكنك المحاولة مرة أخرى</span>
            </div>
          )}

          {isProcessing && (
            <div className="pm-banner pm-banner-processing animate-fade-in">
              <div className="pm-spinner-small" />
              <span>جاري معالجة الدفع، يرجى الانتظار...</span>
            </div>
          )}

          {/* Order Card */}
          <div className="pm-card animate-fade-in">
            <div className="pm-card-header">
              <h4>تفاصيل الطلب</h4>
              <span className={`pm-status-chip ${isPaid ? "pm-chip-success" : isFailed ? "pm-chip-error" : isProcessing ? "pm-chip-processing" : "pm-chip-pending"}`}>
                {isPaid ? "مدفوع" : isFailed ? "فشل" : isProcessing ? "قيد المعالجة" : "بانتظار الدفع"}
              </span>
            </div>

            {/* Meta */}
            <div className="pm-meta-row">
              <div className="pm-meta-item">
                <span className="pm-meta-label">رقم الطلب</span>
                <span className="pm-meta-value">{payment.qi?.merchantReference || payment.invoiceNumber}</span>
              </div>
              <div className="pm-meta-item">
                <span className="pm-meta-label">العميل</span>
                <span className="pm-meta-value">{payment.customer?.name}</span>
              </div>
            </div>

            <div className="pm-divider" />

            {/* Items */}
            <div className="pm-items">
              {payment.items?.map((item, idx) => (
                <div className="pm-item-row" key={idx}>
                  <div className="pm-item-info">
                    <span className="pm-item-name">{item.name}</span>
                    {(item.quantity || 1) > 1 && (
                      <span className="pm-item-qty">x{item.quantity}</span>
                    )}
                  </div>
                  <span className="pm-item-price">{formatPrice(item.price * (item.quantity || 1))}</span>
                </div>
              ))}
            </div>

            <div className="pm-divider" />

            {/* Total */}
            <div className="pm-total-row">
              <span className="pm-total-label">المبلغ الإجمالي</span>
              <span className="pm-total-value">{formatPrice(payment.totalAmount)}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pm-actions animate-fade-in">
            {!isPaid && !isProcessing && (
              <button
                className="pm-btn pm-btn-pay"
                onClick={handlePay}
                disabled={paying}
              >
                {paying ? (
                  <>
                    <div className="pm-spinner-small pm-spinner-dark" />
                    <span>جاري التحويل...</span>
                  </>
                ) : (
                  <>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                    <span>ادفع الآن عبر Qi Card</span>
                  </>
                )}
              </button>
            )}

            {isProcessing && (
              <button className="pm-btn pm-btn-secondary" onClick={() => window.location.reload()}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                <span>تحديث حالة الدفع</span>
              </button>
            )}

            {isPaid && (
              <button className="pm-btn pm-btn-primary" onClick={() => navigate(`/invoice/${accessToken}`)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                <span>عرض الفاتورة</span>
              </button>
            )}

            {!isPaid && (
              <button className="pm-btn pm-btn-outline" onClick={handleShare}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                <span>مشاركة رابط الدفع</span>
              </button>
            )}
          </div>

          {/* Security Footer */}
          <div className="pm-footer">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            <span>رابط دفع آمن ومشفّر من النخبة ستور</span>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default PayLink;
